/**
 * UptimeGrid — the design's 90-day uptime wall: one column per day, three
 * eight-hour blocks per column, time-of-day labels on the left and month
 * labels underneath. Olive = operational; degraded and incident cells carry
 * status tints with an inset ring. Fed by the uptime contract via
 * `toUptimeGrid` / `uptimeMonthLabels` in contracts.ts.
 */

import { useState } from 'react'

import type { UptimeCell, UptimeImpact, UptimeLevel } from '@/components/models/home/contracts'
import { outageSpanLabel } from '@/components/models/home/contracts'
import { anchorStyle, useCursorAnchor } from '@/components/models/home/cursorAnchor'

/** Row labels — the three eight-hour blocks each day is split into. */
const UPTIME_BLOCK_LABELS = ['12a–8a', '8a–4p', '4p–12a']

const LEVEL_CLASSES: Record<UptimeLevel, string> = {
  0: 'bg-secondary',
  // Fallback active: solid amber — "working, but not on the primary".
  // Distinct from degraded's faint tint + ring, which means users saw errors.
  1: 'bg-warning/60 ring-1 ring-inset ring-warning',
  2: 'bg-warning/20 ring-1 ring-inset ring-warning',
  3: 'bg-destructive/10 ring-1 ring-inset ring-destructive',
}

const LEVEL_LABELS: Record<UptimeLevel, string> = {
  0: 'Operational',
  1: 'Fallback active',
  2: 'Degraded',
  3: 'Incident',
}

export function UptimeLegend(): React.JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
      {([0, 1, 2, 3] as UptimeLevel[]).map((level) => (
        <span key={level} className="inline-flex items-center gap-1.5">
          <span className={`h-2.5 w-2.5 rounded-sm ${LEVEL_CLASSES[level]}`} />
          {LEVEL_LABELS[level]}
        </span>
      ))}
    </div>
  )
}

/** What each cause means in one line, in the reader's terms. */
const CAUSE_LABELS: Record<UptimeImpact['cause'], string> = {
  provider: 'Provider outage',
  trainloop: 'TrainLoop outage',
  errors: 'Elevated errors',
  fallback: 'Fallback active · primary failing probes',
  probes: 'Probes failing · primary still serving',
  unknown: 'Down · cause not recorded',
}

interface UptimeGridProps {
  /** One entry per day, each holding three eight-hour blocks. */
  days: UptimeCell[][]
  /** Month labels spread under the grid, oldest first. */
  months: string[]
  /**
   * Drill-down for blocks with fallback-served requests: clicking one
   * filters the request log to those requests (TRA-923). Blocks without
   * fallbacks stay non-interactive beyond hover/focus.
   */
  onFallbackClick?: (cell: UptimeCell) => void
}

/** Readout size, needed up front so it can flip away from the edges. */
const READOUT_WIDTH = 256
const READOUT_HEIGHT = 108

/** What the hover readout says about one block, as an accessible name. */
function cellReadout(cell: UptimeCell): string {
  const parts = [`${cell.label}: ${LEVEL_LABELS[cell.level]}`]
  const span = outageSpanLabel(cell)
  if (span !== null) {
    parts.push(`failures ${span}`)
  } else if (cell.outageBlocks > 1) {
    parts.push(`part of a ${(cell.outageBlocks * 8).toString()}-hour outage`)
  }
  for (const impact of cell.impacts) {
    parts.push(`${impact.model}: ${CAUSE_LABELS[impact.cause]}`)
  }
  if (cell.fallbacks > 0) {
    parts.push(`${cell.fallbacks.toLocaleString()} served by fallback`)
  }
  parts.push(`${cell.requests.toLocaleString()} requests`)
  return parts.join(', ')
}

export function UptimeGrid({ days, months, onFallbackClick }: UptimeGridProps): React.JSX.Element {
  const { anchor, track, clear } = useCursorAnchor()
  const [hovered, setHovered] = useState<{ day: number; block: number } | null>(null)
  const cell = hovered === null ? undefined : days[hovered.day]?.[hovered.block]

  // With no blocks the wall renders as three bare row labels and nothing
  // else, which looks like a broken chart rather than an absence of data.
  if (days.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No availability recorded yet. The gateway probes a model once it is
        deployed, and the wall fills in from there.
      </p>
    )
  }

  return (
    // `relative` lives OUTSIDE the scrolling wall: an absolutely positioned
    // child of an `overflow-x-auto` box is clipped by it, which cut the
    // readout off at the chart edge.
    <div
      className="relative"
      onMouseMove={track}
      onMouseLeave={() => {
        clear()
        setHovered(null)
      }}
    >
      {cell && anchor && (
        <div
          className="pointer-events-none absolute z-20 border border-border bg-card p-3"
          style={anchorStyle(anchor, READOUT_WIDTH, READOUT_HEIGHT)}
        >
          <p className="flex items-baseline justify-between gap-3 font-mono text-[10px] uppercase tracking-wider">
            <span className="text-foreground">{cell.label}</span>
            <span className="text-muted-foreground">{LEVEL_LABELS[cell.level]}</span>
          </p>
          {cell.outageBlocks > 0 && (
            <p className="mt-1.5 text-xs">
              <span className="text-muted-foreground">Failures </span>
              <span className="font-mono tabular-nums">
                {/* When failed probes were SEEN (first→last), plus how long
                    the block was down inside that window — the two differ
                    when probes failed sparsely (TRA-952). Falls back to the
                    8h-block arithmetic when the gateway recorded no span. */}
                {outageSpanLabel(cell) ?? `${(cell.outageBlocks * 8).toString()}h window`}
              </span>
            </p>
          )}
          {cell.impacts.length === 0 ? (
            <p className="mt-2 text-xs text-muted-foreground">
              {cell.requests.toLocaleString()} requests, no failures.
            </p>
          ) : (
            <ul className="mt-2 space-y-1 text-xs">
              {cell.impacts.map((impact) => (
                <li key={`${impact.model}-${impact.cause}`} className="flex flex-col">
                  <span className="font-mono text-[11px]">{impact.model}</span>
                  <span className="text-muted-foreground">{CAUSE_LABELS[impact.cause]}</span>
                </li>
              ))}
            </ul>
          )}
          {cell.fallbacks > 0 && (
            <p className="mt-1.5 text-xs">
              <span className="font-mono tabular-nums">
                {cell.fallbacks.toLocaleString()}
              </span>
              <span className="text-muted-foreground">
                {' '}of {cell.requests.toLocaleString()} requests served by
                fallback{onFallbackClick ? ' (click to inspect)' : ''}
              </span>
            </p>
          )}
        </div>
      )}
      <div className="flex items-start gap-2.5">
        <div className="flex flex-none flex-col gap-0.5 pt-px">
          {UPTIME_BLOCK_LABELS.map((label) => (
            <span
              key={label}
              className="h-[9px] w-10 text-right text-[9px] leading-[9px] text-muted-foreground"
            >
              {label}
            </span>
          ))}
        </div>
        <div className="min-w-0 flex-1">
          {/* Not `role="img"`: a single image label can say "90 days of
              uptime" and nothing about WHICH day broke, so everything the
              hover readout carries would be pointer-only. Each block is a
              focusable button naming its own state instead, and the list
              semantics keep them countable. */}
          {/* One equal-width column per day, stretched to the card: fixed
              9px squares overflow a ~800px card at 90 days, and a scrollbar
              on an availability wall reads as a broken chart. Cells give up
              exact squareness to always fit. */}
          <div
            className="grid auto-cols-fr grid-flow-col gap-0.5"
            role="list"
            aria-label="Uptime in eight-hour blocks"
          >
            {days.map((blocks, day) => (
              <div key={day} className="flex flex-col gap-0.5" role="listitem">
                {blocks.map((cellData, block) => {
                  const drillable =
                    onFallbackClick !== undefined && cellData.fallbacks > 0
                  return (
                    <button
                      key={block}
                      type="button"
                      // No `title`: the browser tooltip fires a second later,
                      // on top of the readout, saying the same thing worse.
                      aria-label={cellReadout(cellData)}
                      onMouseEnter={() => {
                        setHovered({ day, block })
                      }}
                      onFocus={() => {
                        setHovered({ day, block })
                      }}
                      onClick={
                        drillable
                          ? () => { onFallbackClick(cellData) }
                          : undefined
                      }
                      className={`h-[9px] w-full min-w-[3px] rounded-[2px] outline-offset-2 ${
                        drillable ? 'cursor-pointer' : 'cursor-default'
                      } ${LEVEL_CLASSES[cellData.level]}`}
                    />
                  )
                })}
              </div>
            ))}
          </div>
          <div className="mt-2.5 flex justify-between font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            {months.map((month) => (
              <span key={month}>{month}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
